import React from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    FormControlLabel,
    Grid,
    IconButton,
    Radio,
    Typography,
    useTheme
} from "@mui/material";
import {DescriptionRounded, ExpandMore} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {IRaDetailView} from "../../models/models";
import { FormAutocomplete, FormDateTimePicker, FormRadioGroup, FormTextField, FormCheckbox } from "../../common/form-fields-wrappers";
import { useFormContext } from "react-hook-form";
import { RA_STATUS_OPTIONS } from "../../ra-search/config";
import {useNavigate} from "react-router-dom";

const GeneralInfo = () => {
    const {t} = useTranslation(['network', 'translation']);
    const theme = useTheme();
    const navigate = useNavigate();

    const raDetailForm = useFormContext<IRaDetailView>();

    const raStatusOptions = RA_STATUS_OPTIONS;

    const handleReservationNumberAction = () => {
        const bookingId = raDetailForm.getValues('generalInfo.reservationNumber');
        navigate(`/booking/view/${bookingId}`);
    }

    return (
            <Accordion defaultExpanded={true}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant={"bodyLarge"}>
                        {t("raDetail.generalInfo.accordion.title")}
                    </Typography>
                </AccordionSummary>

                <AccordionDetails>
                    <Grid container columnSpacing={2} sx={{mb: theme.spacing(4)}}>
                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IRaDetailView>
                                name='generalInfo.raNumber'
                                control={raDetailForm.control}
                                label={t("raDetail.generalInfo.form.label.raNumber")}
                                fullWidth
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormAutocomplete<IRaDetailView>
                                name='generalInfo.dmsIdentificationCode'
                                control={raDetailForm.control}
                                label={t("raDetail.generalInfo.form.label.dmsIdentificationCode")}
                                fullWidth
                                options={raStatusOptions}
                                isOptionEqualToValue={(option: any, value: any) => option?.code === value?.code}
                                getOptionLabel={(opt: any) => opt?.description}
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormDateTimePicker<IRaDetailView>
                                name="generalInfo.pickupDate"
                                control={raDetailForm.control}
                                label={t("raDetail.generalInfo.form.label.pickupDate")}
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IRaDetailView>
                                name='generalInfo.renewalsAndSubstitutions'
                                control={raDetailForm.control}
                                label={t("raDetail.generalInfo.form.label.renewalsAndSubstitutions")}
                                fullWidth
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormAutocomplete<IRaDetailView>
                                name="generalInfo.raStatus"
                                control={raDetailForm.control}
                                label={t("raDetail.generalInfo.form.label.raStatus")}
                                fullWidth
                                options={raStatusOptions}
                                isOptionEqualToValue={(option: any, value: any) => option?.code === value?.code}
                                getOptionLabel={(opt: any) => opt?.description}
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IRaDetailView>
                                name='generalInfo.dmsOperator'
                                control={raDetailForm.control}
                                label={t("raDetail.generalInfo.form.label.dmsOperator")}
                                fullWidth
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "flex-end"}}>
                            <FormTextField<IRaDetailView>
                                name='generalInfo.reservationNumber'
                                control={raDetailForm.control}
                                label={t("raDetail.generalInfo.form.label.reservationNumber")}
                                fullWidth
                                disabled
                            />
                            <IconButton title={t('raDetail.generalInfo.form.icon.reservationNumberDetail')}
                                        color="accent"
                                        onClick={handleReservationNumberAction}
                                        sx={{mx: theme.spacing(0.5)}}>
                                <DescriptionRounded />
                            </IconButton>
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IRaDetailView>
                                name='generalInfo.dossier'
                                control={raDetailForm.control}
                                label={t("raDetail.generalInfo.form.label.dossier")}
                                fullWidth
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IRaDetailView>
                                name='generalInfo.totalRentalDays'
                                control={raDetailForm.control}
                                label={t("raDetail.generalInfo.form.label.totalRentalDays")}
                                fullWidth
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} >
                            <FormTextField<IRaDetailView>
                                name='generalInfo.effectiveRentalDays'
                                control={raDetailForm.control}
                                label={t("raDetail.generalInfo.form.label.effectiveRentalDays")}
                                fullWidth
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IRaDetailView>
                                name='generalInfo.mileageIncluded'
                                control={raDetailForm.control}
                                label={t("raDetail.generalInfo.form.label.mileageIncluded")}
                                fullWidth
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "flex-end"}}>
                            <FormCheckbox<IRaDetailView>
                                name="generalInfo.toBeReconciled"
                                label={t("raDetail.generalInfo.form.label.toBeReconciled")}
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IRaDetailView>
                                name='generalInfo.bookedAcriss'
                                control={raDetailForm.control}
                                label={t("raDetail.generalInfo.form.label.bookedAcriss")}
                                fullWidth
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IRaDetailView>
                                name='generalInfo.bookedAcrissModel'
                                control={raDetailForm.control}
                                label={t("raDetail.generalInfo.form.label.bookedAcrissModel")}
                                fullWidth
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IRaDetailView>
                                name='generalInfo.bookedVehicleType'
                                control={raDetailForm.control}
                                label={t("raDetail.generalInfo.form.label.bookedVehicleType")}
                                fullWidth
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormRadioGroup<IRaDetailView>
                                name="generalInfo.upSell"
                                label={t("raDetail.generalInfo.form.label.upSell")}
                                disabled
                            >
                                <FormControlLabel
                                    value={false}
                                    label={t("raDetail.generalInfo.form.label.no")}
                                    control={<Radio />}
                                />
                                <FormControlLabel
                                    value= {true}
                                    label={t("raDetail.generalInfo.form.label.yes")}
                                    control={<Radio />}
                                />
                            </FormRadioGroup>
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IRaDetailView>
                                name='generalInfo.billedCarGroup'
                                control={raDetailForm.control}
                                label={t("raDetail.generalInfo.form.label.billedCarGroup")}
                                fullWidth
                                disabled
                            />
                        </Grid>

                            <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                <FormTextField<IRaDetailView>
                                    name='generalInfo.billedCarGroupModel'
                                    control={raDetailForm.control}
                                    label={t("raDetail.generalInfo.form.label.billedCarGroupModel")}
                                    fullWidth
                                    disabled
                                />
                            </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IRaDetailView>
                                name='generalInfo.rfidCard'
                                control={raDetailForm.control}
                                label={t("raDetail.generalInfo.form.label.rfidCard")}
                                fullWidth
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "flex-end"}}>
                            <FormCheckbox<IRaDetailView>
                                name="generalInfo.keyless"
                                label={t("raDetail.generalInfo.form.label.keyless")}
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IRaDetailView>
                                name='generalInfo.effectiveAcrissId'
                                control={raDetailForm.control}
                                label={t("raDetail.generalInfo.form.label.effectiveAcrissId")}
                                fullWidth
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IRaDetailView>
                                name='generalInfo.effectiveAcrissDescription'
                                control={raDetailForm.control}
                                label={t("raDetail.generalInfo.form.label.effectiveAcrissDescription")}
                                fullWidth
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IRaDetailView>
                                name='generalInfo.plateNumber'
                                control={raDetailForm.control}
                                label={t("raDetail.generalInfo.form.label.plate")}
                                fullWidth
                                disabled
                            />
                        </Grid>

                        <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                            <FormTextField<IRaDetailView>
                                name='generalInfo.vehicleId'
                                control={raDetailForm.control}
                                label={t("raDetail.generalInfo.form.label.vehicleId")}
                                fullWidth
                                disabled
                            />
                        </Grid>
                    </Grid>

                    <Typography variant={"bodyLarge"}>
                        {t("raDetail.generalInfo.form.subform.pickup")}
                    </Typography>
                    <Grid container sx={{mb: theme.spacing(4)}}>
                        <Grid container item columnSpacing={2}>
                            <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                <FormAutocomplete<IRaDetailView>
                                    name="generalInfo.pickupLocation"
                                    control={raDetailForm.control}
                                    label={t("raDetail.generalInfo.form.label.pickupLocation")}
                                    fullWidth
                                    options={[]}
                                    isOptionEqualToValue={(option: any, value: any) => option?.code === value?.code}
                                    getOptionLabel={(opt: any) => opt?.description}
                                    disabled
                                />
                            </Grid>

                            <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                <FormDateTimePicker<IRaDetailView>
                                    name="generalInfo.expectedPickupDate"
                                    control={raDetailForm.control}
                                    label={t("raDetail.generalInfo.form.label.expectedPickupDate")}
                                    disabled
                                />
                            </Grid>

                            <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                <FormDateTimePicker<IRaDetailView>
                                    name="generalInfo.actualRentalStart"
                                    control={raDetailForm.control}
                                    label={t("raDetail.generalInfo.form.label.actualPickupDate")}
                                    disabled
                                />
                            </Grid>
                        </Grid>

                        <Grid container item columnSpacing={2}>
                            <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                <FormTextField<IRaDetailView>
                                    name='generalInfo.kmOnStart'
                                    control={raDetailForm.control}
                                    label={t("raDetail.generalInfo.form.label.kmOnStart")}
                                    fullWidth
                                    disabled
                                />
                            </Grid>

                            <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                <FormTextField<IRaDetailView>
                                    name='generalInfo.tankLevelOnStart'
                                    control={raDetailForm.control}
                                    label={t("raDetail.generalInfo.form.label.tankLevelOnStart")}
                                    fullWidth
                                    disabled
                                />
                            </Grid>

                            <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                <FormTextField<IRaDetailView>
                                    name='generalInfo.batteryOnStart'
                                    control={raDetailForm.control}
                                    label={t("raDetail.generalInfo.form.label.batteryOnStart")}
                                    fullWidth
                                    disabled
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    <Typography variant={"bodyLarge"} >
                        {t("raDetail.generalInfo.form.subform.checkout")}
                    </Typography>
                    <Grid container sx={{mb: theme.spacing(4)}}>
                        <Grid container item columnSpacing={2}>
                            <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                <FormTextField<IRaDetailView>
                                    name='generalInfo.kmsOnCheck'
                                    control={raDetailForm.control}
                                    label={t("raDetail.generalInfo.form.label.kmsOnCheck")}
                                    fullWidth
                                    disabled
                                />
                            </Grid>

                            <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                <FormTextField<IRaDetailView>
                                    name='generalInfo.tankLevelOnCheck'
                                    control={raDetailForm.control}
                                    label={t("raDetail.generalInfo.form.label.tankLevelOnCheck")}
                                    fullWidth
                                    disabled
                                />
                            </Grid>

                            <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                <FormTextField<IRaDetailView>
                                    name='generalInfo.batteryOnCheck'
                                    control={raDetailForm.control}
                                    label={t("raDetail.generalInfo.form.label.batteryOnCheck")}
                                    fullWidth
                                    disabled
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    <Typography variant={"bodyLarge"} >
                        {t("raDetail.generalInfo.form.subform.dropoff")}
                    </Typography>
                    <Grid container>
                        <Grid container item columnSpacing={2}>
                            <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                <FormAutocomplete<IRaDetailView>
                                    name="generalInfo.dropoffLocation"
                                    control={raDetailForm.control}
                                    label={t("raDetail.generalInfo.form.label.dropoffLocation")}
                                    fullWidth
                                    options={[]}
                                    isOptionEqualToValue={(option: any, value: any) => option?.code === value?.code}
                                    getOptionLabel={(opt: any) => opt?.description}
                                    disabled
                                />
                            </Grid>

                            <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                <FormDateTimePicker<IRaDetailView>
                                    name="generalInfo.expectedDropoffDate"
                                    control={raDetailForm.control}
                                    label={t("raDetail.generalInfo.form.label.expectedDropoffDate")}
                                    disabled
                                />
                            </Grid>

                            <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                <FormDateTimePicker<IRaDetailView>
                                    name="generalInfo.actualDropoffDate"
                                    control={raDetailForm.control}
                                    label={t("raDetail.generalInfo.form.label.actualDropoffDate")}
                                    disabled
                                />
                            </Grid>

                        </Grid>

                        <Grid container item columnSpacing={2}>
                            <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                <FormTextField<IRaDetailView>
                                    name='generalInfo.dropOffKmOnCheck'
                                    control={raDetailForm.control}
                                    label={t("raDetail.generalInfo.form.label.dropOffKmOnCheck")}
                                    fullWidth
                                    disabled
                                />
                            </Grid>

                            <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                <FormTextField<IRaDetailView>
                                    name='generalInfo.dropOffTankLevelOnCheck'
                                    control={raDetailForm.control}
                                    label={t("raDetail.generalInfo.form.label.dropOffTankLevelOnCheck")}
                                    fullWidth
                                    disabled
                                />
                            </Grid>

                            <Grid item xs={6} md={3} sx={{display: "flex", alignItems: "center"}}>
                                <FormTextField<IRaDetailView>
                                    name='generalInfo.dropOffbatteryOnCheck'
                                    control={raDetailForm.control}
                                    label={t("raDetail.generalInfo.form.label.dropOffbatteryOnCheck")}
                                    fullWidth
                                    disabled
                                />
                            </Grid>
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
    );
}

export default GeneralInfo;